import React, {
  ReactNode,
  useCallback,
  useEffect,
  MouseEvent,
  useRef,
} from 'react';
import {
  PopoverProps,
  PopoverContentProps,
  Content as RadixPopoverContent,
  Root as RadixPopoverRoot,
  Portal as RadixPopoverPortal,
  Close as RadixPopoverClose,
} from '@radix-ui/react-popover';
import * as SC from 'components/Modal/Modal.styles';
import styled from 'styled-components';
import { PopoverTrigger } from './PopoverTrigger';

interface Props extends PopoverProps {
  trigger: ReactNode;
  asChild?: boolean;
  toggleOnHover?: boolean;
  hoverDelay?: number;
  showCloseIcon?: boolean;
  portalled?: boolean;
  alignOffset?: number;
  sideOffset?: number;
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
  'aria-modal'?: boolean;
  'aria-label'?: string;
}

export const Popover: React.FC<Props> = ({
  asChild,
  trigger,
  toggleOnHover,
  hoverDelay,
  children,
  showCloseIcon,
  sideOffset,
  alignOffset,
  portalled,
  onOpenChange,
  open,
  align,
  side,
  'aria-modal': ariaModal,
  'aria-label': ariaLabel,
  ...props
}) => {
  const hoverTimeout = useRef<NodeJS.Timeout>(undefined);

  const handleTriggerClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onOpenChange?.(!open);
    },
    [open, onOpenChange],
  );

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeout.current);
    };
  }, []);

  const mouseEventHandler = (isOver: boolean) => {
    if (!toggleOnHover) {
      return;
    }

    clearTimeout(hoverTimeout.current);

    hoverTimeout.current = setTimeout(() => {
      onOpenChange?.(isOver);
    }, hoverDelay || 500);
  };

  const content = (
    <PopoverContent
      onMouseEnter={() => mouseEventHandler(true)}
      onMouseLeave={() => mouseEventHandler(false)}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      align={align}
      side={side}
      aria-modal={ariaModal}
      aria-label={ariaLabel}
    >
      <>
        {children}
        {showCloseIcon ? (
          <CloseIcon className="PopoverClose" aria-label="Close">
            <SC.Close />
          </CloseIcon>
        ) : null}
      </>
    </PopoverContent>
  );

  return (
    <Container>
      <RadixPopoverRoot {...props} open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger
          asChild={asChild}
          trigger={trigger}
          handleTriggerClick={handleTriggerClick}
          mouseEventHandler={mouseEventHandler}
        />
        {portalled ? (
          <RadixPopoverPortal
            container={document.getElementById('kouto-embed-root')}
          >
            {content}
          </RadixPopoverPortal>
        ) : (
          content
        )}
      </RadixPopoverRoot>
    </Container>
  );
};

const CloseIcon = styled(RadixPopoverClose)`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const Container = styled.div`
  position: relative;
`;

const PopoverContent = styled(RadixPopoverContent)`
  z-index: var(--way-design-popoverZIndex);
`;
